import documentIcon from "../assets/icons/document.png";
import downloadIcon from "../assets/icons/download_green.svg";
import applyOnlineIcon from "../assets/icons/apply_online.svg";
import moreInfoIcon from "../assets/icons/more_info.svg";
import { useTranslation } from 'react-i18next';

const DocumentCard = ({
  title,
  description,
  downloadLink,
  sampleFormLink,
  showSampleForm = true,
  showDescription = true,
  // New optional props for service-style card
  status,
  fee,
  processingTime,
  infoLabel = "More info",
  actionLabel = "Apply online",
  onActionClick,
  showServiceButtons = false,
  hideIcon = false
}) => {
  const { i18n } = useTranslation();
  const handleDownload = () => {
    if (!downloadLink) return;
    
    try {
      // Extract filename from URL (decode URL-encoded names)
      const fileName = decodeURIComponent(downloadLink.split("/").pop().split("?")[0] || 'document.pdf');
      
      // Create a temporary anchor element with download attribute
      const anchor = document.createElement("a");
      anchor.href = downloadLink;
      anchor.download = fileName;
      anchor.target = "_blank";
      anchor.rel = "noopener noreferrer";
      
      // Add to DOM temporarily
      document.body.appendChild(anchor);
      
      // Trigger click
      anchor.click();
      
      // Clean up
      document.body.removeChild(anchor);
      
    } catch (e) {
      console.warn('Download failed:', e);
      // Ultimate fallback: direct navigation
      window.location.href = downloadLink;
    }
  };

  const handleSampleForm = () => {
    // If no sampleFormLink provided, fall back to main download link
    const linkToUse = sampleFormLink || downloadLink;
    
    if (!linkToUse) {
      console.warn('No sample form link or download link provided');
      return;
    }
    
    console.log('Sample form link:', linkToUse);
    
    try {
      // Check if it's a local file (starts with /assets or assets)
      const isLocalFile = linkToUse.startsWith('/assets') || linkToUse.startsWith('assets');
      console.log('Is local file:', isLocalFile);
      
      if (isLocalFile) {
        // For local files, construct the public path
        const publicPath = linkToUse.startsWith('/') ? linkToUse : `/${linkToUse}`;
        console.log('Public path:', publicPath);
        
        // Extract filename
        const fileName = linkToUse.split("/").pop() || 'document.pdf';
        console.log('Filename:', fileName);
        
        // Create a temporary anchor element
        const anchor = document.createElement("a");
        const fullUrl = `${process.env.PUBLIC_URL}${publicPath}`;
        console.log('Full URL:', fullUrl);
        
        anchor.href = fullUrl;
        anchor.download = fileName;
        anchor.target = "_blank";
        anchor.rel = "noopener noreferrer";
        
        // Add to DOM temporarily
        document.body.appendChild(anchor);
        
        // Trigger click
        anchor.click();
        
        // Clean up
        document.body.removeChild(anchor);
      } else {
        // For external URLs, use the original logic
        const fileName = decodeURIComponent(linkToUse.split("/").pop().split("?")[0] || 'document.pdf');
        
        const anchor = document.createElement("a");
        anchor.href = linkToUse;
        anchor.download = fileName;
        anchor.target = "_blank";
        anchor.rel = "noopener noreferrer";
        
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
      }
      
    } catch (e) {
      console.warn('Sample form download failed:', e);
      // Ultimate fallback: direct navigation
      const finalUrl = linkToUse.startsWith('/assets') || linkToUse.startsWith('assets') 
        ? `${process.env.PUBLIC_URL}/${linkToUse.replace(/^\//, '')}`
        : linkToUse;
      console.log('Fallback URL:', finalUrl);
      window.open(finalUrl, '_blank');
    }
  };

  const handleAction = () => {
    if (typeof onActionClick === "function") {
      onActionClick();
    } else if (downloadLink) {
      handleDownload();
    }
  };

  return (
    <div className="border border-[#DDDDDD] rounded-md p-4 bg-white flex flex-col gap-1 h-full">
      {/* Content Container - takes up available space */}
      <div className="flex-1 flex flex-col gap-1">
        <div className="flex flex-col gap-2">
          <div className="flex flex-col items-start gap-3">
            {!hideIcon && (
              <img
                className="w-7 h-7 relative bottom-1"
                src={documentIcon}
                alt="Document Icon"
              />
            )}
            <h1
              className="text-xl font-semibold w-full h-fit max-sm:text-lg"
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minHeight: '2.6em', // Ensures two lines height even for short titles
                lineHeight: '1.3',
                wordBreak: 'break-word',
              }}
            >
              {title}
            </h1>
          </div>

          {/* Optional status badge */}
          {status && (
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[var(--color-primary)] block" />
              <span className="text-sm font-medium text-[var(--color-primary)]">{status}</span>
            </div>
          )}
        </div>
        {showDescription && <h3 className="text-xs text-[#727272]">{description}</h3>}

        {/* Optional fee / processing time row */}
        {(fee || processingTime) && (
          <div className="mt-auto grid grid-cols-2 gap-4 text-sm mb-2">
            <div className="flex flex-col">
              <span className="text-xs text-[#727272]">{i18n.language === 'mr' ? 'शुल्क' : 'Fee'}</span>
              <span className="font-semibold">{fee || "-"}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-[#727272]">{i18n.language === 'mr' ? 'प्रक्रिया काल' : 'Process Days'}</span>
              <span className="font-semibold">{processingTime || "-"}</span>
            </div>
          </div>
        )}
      </div>

      {/* Buttons Container - always at bottom */}
      <div className="flex flex-col gap-2 w-full">
        {/* Service-style buttons (info + primary) */}
        {showServiceButtons ? (
          <>
            <button
              onClick={() => {
                // try to use sample form as "info" link if provided, otherwise no-op
                if (sampleFormLink) handleSampleForm();
              }}
              className="border border-[var(--color-primary)] hover:bg-[var(--color-bg-gradient)] p-2 flex items-center justify-center gap-2 text-sm font-medium text-[var(--color-primary)] w-full rounded-md transition"
            >
              <div 
                style={{
                  width: '16px',
                  height: '16px',
                  backgroundColor: 'var(--color-primary)',
                  WebkitMaskImage: `url(${moreInfoIcon})`,
                  WebkitMaskSize: 'contain',
                  WebkitMaskRepeat: 'no-repeat',
                  WebkitMaskPosition: 'center',
                  maskImage: `url(${moreInfoIcon})`,
                  maskSize: 'contain',
                  maskRepeat: 'no-repeat',
                  maskPosition: 'center'
                }}
                aria-hidden="true"
              />
              {infoLabel}
            </button>

            <button
              onClick={handleAction}
              className="bg-[var(--color-primary)] hover:bg-[var(--color-primary-dark)] p-2 flex items-center justify-center gap-2 text-sm font-medium text-white w-full rounded-md transition"
            >
              <img loading="lazy" src={applyOnlineIcon} alt="Apply Online" className="w-4 h-4 filter brightness-0 invert" />
              {actionLabel}
            </button>
          </>
        ) : (
          <>
            {/* Sample Form Button */}
            {showSampleForm && (
              <button
                onClick={handleSampleForm}
                className="border border-[var(--color-primary)] hover:bg-[var(--color-bg-gradient)] p-2 flex items-center justify-center gap-2 text-sm font-medium text-[var(--color-primary)] w-full rounded-md transition"
              >
                <div 
                  style={{
                    width: '16px',
                    height: '16px',
                    backgroundColor: 'var(--color-primary)',
                    WebkitMaskImage: `url(${downloadIcon})`,
                    WebkitMaskSize: 'contain',
                    WebkitMaskRepeat: 'no-repeat',
                    WebkitMaskPosition: 'center',
                    maskImage: `url(${downloadIcon})`,
                    maskSize: 'contain',
                    maskRepeat: 'no-repeat',
                    maskPosition: 'center'
                  }}
                  aria-hidden="true"
                />
                Sample Form
              </button>
            )}
            
            {/* Download Button */}
            <button
              onClick={handleDownload}
              className="bg-[var(--color-primary)] hover:bg-[var(--color-primary-dark)] p-2 flex items-center justify-center gap-2 text-sm font-medium text-white w-full rounded-md transition"
            >
              <img loading="lazy" className="w-4 h-4 filter brightness-0 invert" src={downloadIcon} alt="Download Icon" />
              Download
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default DocumentCard;
